/* eslint-disable react/no-unescaped-entities */
import { Link, graphql, useStaticQuery } from 'gatsby'
import React from 'react'
import { getImage } from 'gatsby-plugin-image'
import JobSeekerHeading from '../JobSeekerHeading'
import styles from './JobSeekersGuide.module.scss'
import { ShowGatsbyImage } from '../JobTestSticky'

const data = [
  {
    title: 'Numerical reasoning tests',
    desc: [
      <p>
        These <a href="https://l.linklyhq.com/l/O0fY">tests</a> require you to
        answer questions based on <span>statistics, figures and charts.</span>
      </p>,
    ],
    img: '/numerical.svg',
  },
  {
    title: 'Verbal reasoning tests',
    desc: [
      <p>
        A means of assessing your{' '}
        <span>
          <a href="https://l.linklyhq.com/l/J3UH">verbal</a> logic
        </span>{' '}
        and capacity to quickly digest information from passages of text.
      </p>,
    ],
    img: '/verbal.svg',
  },
  {
    title: 'Intray exercises',
    desc: [
      <p>
        A business-related scenario that assesses how well you can{' '}
        <span>prioritise tasks.</span>
      </p>,
    ],
    img: '/intray.svg',
  },
  {
    title: 'Diagrammatic tests ',
    desc: [
      <p>
        <a href="https://l.linklyhq.com/l/O0fY">Tests</a> that measure your{' '}
        <Link to="/practice-logical-reasoning-test">logical reasoning</Link>,
        usually under <span>strict time conditions.</span>
      </p>,
    ],
    img: '/diargrammatic.svg',
  },
  {
    title: 'Situational judgement tests ',
    desc: [
      <p>
        Psychological <a href="https://l.linklyhq.com/l/O0fY">tests</a> that
        assess your judgement in resolving <span>work-based problems.</span>
      </p>,
    ],
    img: '/situational.svg',
  },
  {
    title: 'Inductive reasoning tests ',
    desc: [
      <p>
        <a href="https://l.linklyhq.com/l/O0fY">Tests</a> that identify how well
        a candidate can see the <span>underlying logic in patterns,</span>{' '}
        rather than words or numbers.
      </p>,
    ],
    img: '/inductive.svg',
  },
  {
    title: 'Cognitive ability tests ',
    desc: [
      <p>
        A measurement of <span>general intelligence,</span> covering many
        categories of <Link to="/practice-aptitude-tests">aptitude test</Link>.
      </p>,
    ],
    img: '/cognitive.svg',
  },
  {
    title: 'Mechanical reasoning tests ',
    desc: [
      <p>
        These assess your ability to apply{' '}
        <a href="https://l.linklyhq.com/l/Jfg3">mechanical</a> or engineering
        principles to problems; they are often used for{' '}
        <span>technical roles.</span>
      </p>,
    ],
    img: '/mechanical.svg',
  },
  {
    title: 'Watson Glaser tests ',
    desc: [
      <p>
        Designed to assess a candidate’s ability to critically consider
        arguments; <span>often used by law firms.</span>
      </p>,
    ],
    img: '/watson.svg',
  },
  {
    title: 'Abstract reasoning tests',
    desc: [
      <p>
        Another name for{' '}
        <span>
          <a href="/practice-inductive-test">inductive reasoning tests</a>.
        </span>
      </p>,
    ],
    img: '/abstract.svg',
  },
  {
    title: 'Spatial awareness tests ',
    desc: [
      <p>
        These <a href="https://l.linklyhq.com/l/O0fY">tests</a> assess your
        capacity to <span>mentally manipulate images,</span> and are often used
        in applications for jobs in design, engineering and architecture.
      </p>,
    ],
    img: '/spatial.svg',
  },
  {
    title: 'Error checking tests ',
    desc: [
      <p>
        An unusual type of{' '}
        <Link to="/practice-aptitude-tests">aptitude test</Link> that focuses on
        your ability to identify errors in <span>complex data sets.</span>
      </p>,
    ],
    img: '/error-checking.svg',
  },
]

const JobSeekersGuide = () => {
  const imageFiles = useStaticQuery(graphql`
    query {
      allFile(filter: { extension: { regex: "/(png)/" } }) {
        edges {
          node {
            base
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }
  `)

  const imageMap = {}
  imageFiles?.allFile?.edges?.forEach((img) => {
    imageMap[img.node.base] = img.node.childImageSharp.gatsbyImageData
  })

  return (
    <div className={styles.main}>
      <div className={`${styles.container} pb-5 `}>
        <div className={`${styles.inner} py-3 px-1`}>
          <div className="d-flex justify-content-center">
            <img src="/book.svg" alt="book" width={150} height={150} />
          </div>
          <div>
            <h2 className={`${styles.title1} text-center `}>
              THE JOBSEEKER'S GUIDE TO
            </h2>
            <h2 className={`${styles.title2} text-center `}>APTITUDE TESTS</h2>
            <div className={styles.section}>
              <div className="m-4 text-center">
                An <Link to="/practice-aptitude-tests">aptitude test</Link> is a
                systematic means of testing a{' '}
                <span>job candidate’s abilities</span> to perform specific tasks
                and react to a range of different situations.
              </div>
            </div>
            <div className={`${styles.imgRow} row w-100 mx-auto`}>
              <div className="col-sm-4">
                <ShowGatsbyImage
                  alt="standardised-method"
                  image={getImage(imageMap['standardised-method.png'])}
                />
                <div className={`${styles.text} text-center `}>
                  The{' '}
                  <a rel="nofollow" href="https://l.linklyhq.com/l/O0fY">
                    tests
                  </a>{' '}
                  each have a <span>standardised method of administration</span>{' '}
                  and scoring, with the results quantified and compared with all
                  other{' '}
                  <a rel="nofollow" href="https://l.linklyhq.com/l/O0fY">
                    test
                  </a>{' '}
                  takers.
                </div>
              </div>
              <div className="col-sm-4">
                <ShowGatsbyImage
                  alt="prior-knowledge"
                  image={getImage(imageMap['prior-knowledge.png'])}
                />
                <div className={`${styles.text} text-center `}>
                  <span>No prior knowledge is assumed</span>, as employers seek
                  to determine innate ability at a particular competency.
                </div>
              </div>
              <div className="col-sm-4">
                <ShowGatsbyImage
                  alt="online-ortesting-centre"
                  image={getImage(imageMap['online-ortesting-centre.png'])}
                />
                <div className={`${styles.text} text-center `}>
                  Aptitude{' '}
                  <a rel="nofollow" href="https://l.linklyhq.com/l/O0fY">
                    tests
                  </a>{' '}
                  are usually taken <span>online or at a testing centre</span>,
                  such as a firm’s offices, where they can be electronic or
                  paper-based.
                </div>
              </div>
            </div>
          </div>
          <div
            className={`${styles.Hcontainer} mt-5 d-flex align-items-center justify-content-center px-5 `}
          >
            <div className={styles.tHeading}>
              <h3>The secret to not being intimidated by tests?</h3>
            </div>
            <div className="ms-3">
              <h3 className={styles.prep}>Preparation</h3>
            </div>
          </div>
          {/* TYPES OF APTITUDE TEST SECTION */}
          {/* <JobSeekerHeading
            title="What Are The Different Types Of Aptitude Assessments?"
            subHeading="These are the most common types of aptitude test that you will encounter:"
          />
          <div className="row p-lg-5 w-100 mx-auto">
            {data.map((e) => {
              return (
                <div key={e.title} className="col-sm-6">
                  <div className={`${styles.testType} d-flex `}>
                    <div>
                      <img
                        src={e.img}
                        alt={e.title}
                        width="100%"
                        height="100%"
                      />
                    </div>
                    <div>
                      <h3>{e.title}</h3>
                      {e.desc}
                    </div>
                  </div>
                </div>
              )
            })}
          </div> */}
          <div className={`${styles.testType} d-flex px-lg-5 pt-5`}>
            <div>
              <img
                src="/test-structure.svg"
                alt="test-structure"
                width="100%"
                height="100%"
              />
            </div>
            <div>
              <h3>Test Structure for Aptitude Tests</h3>
              <p>
                <a href="https://l.linklyhq.com/l/O0fY">Tests</a> are timed and
                are typically{' '}
                <span class="highlight txt--color-tertiary">
                  multiple choice.
                </span>{' '}
                It is not uncommon for some available answers to be deliberately
                misleading, so you must take care as you work through.
              </p>
              <p>
                Some <a href="https://l.linklyhq.com/l/O0fY">tests</a> escalate
                in difficulty as they progress. Typically these{' '}
                <a href="https://l.linklyhq.com/l/O0fY">tests</a> are not
                designed to be finished by candidates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default JobSeekersGuide
